import React, { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import classNames from 'classnames';
import { useSports } from 'containers/Mundial2026/context/SportsProvider';
import styles from './styles.module.scss';
import PossessionBar from './components/PossessionBar';
import StatThinBar from './components/StatThinBar';
import { dualBarFlexParts, formatStatValue, mapMatchDetailsToMatchStats } from './constants';
import { getTranslationEmptyOrNullKey } from 'utils/getTranslation';
import { renderMaxLengthText } from 'utils/renderMaxLengthText';

const TRANSLATION_KEYS = {
  possessionTitle: 'eventodeportivo_partidos_estadisticas_texto_titulo_posesion',
};

const STAT_ROW_LABEL_KEYS = {
  shotsTotal: 'eventodeportivo_partidos_estadisticas_texto_titulo_tirostotales',
  shotsOnGoal: 'eventodeportivo_partidos_estadisticas_texto_titulo_tirosporteria',
  shotsWide: 'eventodeportivo_partidos_estadisticas_texto_titulo_tirosfuera',
  shotsBlocked: 'eventodeportivo_partidos_estadisticas_texto_titulo_tirosbloqueados',
  savesByGoalkeeper: 'eventodeportivo_partidos_estadisticas_texto_titulo_paradasportero',
  cardsRed: 'eventodeportivo_partidos_estadisticas_texto_titulo_tarjetasrojas',
  cardsYellow: 'eventodeportivo_partidos_estadisticas_texto_titulo_tarjetasamarillas',
  fouls: 'eventodeportivo_partidos_estadisticas_texto_titulo_faltas',
  tacklesWon: 'eventodeportivo_partidos_estadisticas_texto_titulo_entradasganadas',
  passesTotal: 'eventodeportivo_partidos_estadisticas_texto_titulo_pasestotales',
  passAccuracy: 'eventodeportivo_partidos_estadisticas_texto_titulo_precisionpases',
  corners: 'eventodeportivo_partidos_estadisticas_texto_titulo_saquesequina',
  offsides: 'eventodeportivo_partidos_estadisticas_texto_titulo_fuerajuego',
};

const POSSESSION_TITLE_MAX_LENGTH = 23;
const STAT_ROW_MAX_LENGTH = 33;

function renderStatRow({ statKey, label, home, away, isPercent }, bindHeaderTooltip) {
  const noData = dualBarFlexParts(home, away).isEmpty;
  return (
    <li
      key={statKey || label}
      className={classNames(
        styles['wcestadisticas-partido__stat-row'],
        styles['wcestadisticas-partido__stat-row--listed'],
        noData && styles['wcestadisticas-partido__stat-row--no-data'],
      )}
    >
      <span className={styles['wcestadisticas-partido__stat-home']}>{formatStatValue(home, isPercent)}</span>
      <span
        className={styles['wcestadisticas-partido__stat-label']}
        {...(bindHeaderTooltip ? bindHeaderTooltip(label) : {})}
      >
        {label}
      </span>
      <span className={styles['wcestadisticas-partido__stat-away']}>{formatStatValue(away, isPercent)}</span>
      <StatThinBar home={home} away={away} />
    </li>
  );
}

/**
 * @param {object|undefined} matchDetailsData Cuerpo `data` del servicio; estadísticas desde `teamStatistics[0]`.
 */
export default function WCEstadisticasPartido({ matchDetailsData }) {
  const {
    setEstadisticasLiveFeedActive,
    setLineupsLiveFeedActive,
    setLiveEventsLiveFeedActive,
  } = useSports();
  const translations = useSelector(state => state.translations);
  useEffect(() => {
    setEstadisticasLiveFeedActive(true);
    setLineupsLiveFeedActive(true);
    setLiveEventsLiveFeedActive(true);
  }, [
    setEstadisticasLiveFeedActive,
    setLineupsLiveFeedActive,
    setLiveEventsLiveFeedActive,
  ]);

  const homePrimaryHex = matchDetailsData?.matchCard?.[0]?.summary?.matchDetails?.home?.team?.primaryHex;
  const awayPrimaryHex = matchDetailsData?.matchCard?.[0]?.summary?.matchDetails?.away?.team?.primaryHex;

  const texts = useMemo(() => {
    const translationMapper = (key, maxLength) => {
      const text = getTranslationEmptyOrNullKey(TRANSLATION_KEYS[key], translations);
      return maxLength ? renderMaxLengthText(text, maxLength) : text;
    };

    return {
      possessionTitle: translationMapper('possessionTitle', POSSESSION_TITLE_MAX_LENGTH),
    };
  }, [translations]);

  const statRowLabels = useMemo(() => {
    const mapper = (cmsKey) => {
      const text = getTranslationEmptyOrNullKey(cmsKey, translations);
      return renderMaxLengthText(text, STAT_ROW_MAX_LENGTH);
    };

    return Object.fromEntries(
      Object.entries(STAT_ROW_LABEL_KEYS).map(([statKey, cmsKey]) => [statKey, mapper(cmsKey)]),
    );
  }, [translations]);

  const fromDetails = useMemo(
    () => mapMatchDetailsToMatchStats(matchDetailsData),
    [matchDetailsData],
  );

  const rowsForDisplay = useMemo(() => {
    if (!fromDetails?.rows) return [];
    return fromDetails.rows.map((row) => {
      if (!row.statKey) return row;
      const cmsKey = STAT_ROW_LABEL_KEYS[row.statKey];
      if (!cmsKey) return row;
      const translated = statRowLabels[row.statKey];
      return { ...row, label: translated || '' };
    });
  }, [fromDetails, statRowLabels]);

  const splitIdx = rowsForDisplay.findIndex((r) => r.secondaryBand);
  const primaryRows = splitIdx === -1 ? rowsForDisplay : rowsForDisplay.slice(0, splitIdx);
  const secondaryRows = splitIdx === -1 ? [] : rowsForDisplay.slice(splitIdx);

  if (!fromDetails) {
    return null;
  }

  const { home: ph, away: pa } = fromDetails.possession;
  const possessionNoData = dualBarFlexParts(ph, pa).isEmpty;

  return (
    <div
      className={styles['wcestadisticas-partido']}
      style={{
        ...(homePrimaryHex ? { '--wc-estad-home': homePrimaryHex } : {}),
        ...(awayPrimaryHex ? { '--wc-estad-away': awayPrimaryHex } : {}),
      }}
    >
      <div
        className={classNames(styles['wcestadisticas-partido__possession'], {
          [styles['wcestadisticas-partido__possession--no-data']]: possessionNoData,
        })}
      >
        <div
          className={classNames(
            styles['wcestadisticas-partido__stat-row'],
            styles['wcestadisticas-partido__stat-row--possession-head'],
          )}
        >
          <span className={styles['wcestadisticas-partido__stat-home']}>
            {formatStatValue(ph, true)}
          </span>
          <span className={styles['wcestadisticas-partido-title']}>{texts.possessionTitle}</span>
          <span className={styles['wcestadisticas-partido__stat-away']}>
            {formatStatValue(pa, true)}
          </span>
        </div>
        <PossessionBar home={ph} away={pa} />
      </div>

      <ul className={styles['wcestadisticas-partido__list']}>
        {primaryRows.map(row => renderStatRow(row))}
      </ul>
      {secondaryRows.length > 0 ? (
        <ul
          className={classNames(
            styles['wcestadisticas-partido__list'],
            styles['wcestadisticas-partido__list--secondary'],
          )}
        >
          {secondaryRows.map(row => renderStatRow(row))}
        </ul>
      ) : null}
    </div>
  );
}
